/**
 * CompressionHelper
 *
 * Lightweight utility for JSON-based compression and decompression.
 * Uses Deflate + Base64 encoding (compatible with PHP gzdeflate/base64_encode).
 *
 * ---
 * @author SOULITY
 * @email  ho.kim@prione.co.kr
 * @version 1.0.0
 * @license Private
 */

import pako from 'pako';
import { Buffer } from 'buffer';
import _ from 'lodash';

class CompressionHelper {
    #SALT = [9, 15];
    #REPLACER = {
        [String.fromCharCode(47)]: String.fromCharCode(96, 72), // / → `H
        [String.fromCharCode(43)]: String.fromCharCode(42, 84), // + → *T
        [String.fromCharCode(65)]: String.fromCharCode(94, 57), // A → ^9
        [String.fromCharCode(66)]: String.fromCharCode(126, 113), // B → ~q
        [String.fromCharCode(67)]: String.fromCharCode(126, 70), // C → ~F
        [String.fromCharCode(75)]: String.fromCharCode(95, 77), // K → _M
        [String.fromCharCode(82)]: String.fromCharCode(64, 121), // R → @y
        [String.fromCharCode(83)]: String.fromCharCode(64, 101), // S → @e
        [String.fromCharCode(88)]: String.fromCharCode(33, 120), // X → !x
        [String.fromCharCode(90)]: String.fromCharCode(33, 106), // Z → !j
    };

    /**
     * Compress any JSON-serializable data into a Base64 string.
     * @param {any} value - Data to compress.
     * @returns {string} Base64 encoded string.
     * @throws {Error} When serialization or compression fails.
     */
    compressToBase64(value) {
        let json, base64;

        try {
            json = JSON.stringify(value);
        } catch (e) {
            const err = new Error('Failed to serialize data to JSON.');
            err.original = e;
            throw err;
        }

        try {
            const compressed = pako.deflate(json, { level: 6, raw: true });
            base64 = Buffer.from(compressed).toString('base64');
            base64 = _.substitute(base64, this.#REPLACER);
            return (
                _.randomString(this.#SALT[0], { upper: true }) + //
                base64 +
                _.randomString(this.#SALT[1], { upper: true })
            );
        } catch (e) {
            const err = new Error('Compression failed.');
            err.original = e;
            throw err;
        }
    }

    /**
     * Decompress a Base64 string back to its original data.
     * @param {string} base64 - Base64 encoded string.
     * @returns {any} Decompressed data.
     * @throws {Error} When decompression fails.
     */
    decompressFromBase64(base64) {
        try {
            if (!_.isString(base64) || base64.length <= _.sum(this.#SALT)) {
                return '';
            }

            base64 = _.substitute(base64, _.invert(this.#REPLACER));
            base64 = base64.slice(this.#SALT[0], -this.#SALT[1]);
            const binary = Buffer.from(base64, 'base64');
            const json = pako.inflate(binary, { to: 'string', raw: true });

            try {
                return JSON.parse(json);
            } catch {
                return json;
            }
        } catch (e) {
            const err = new Error('Decompression failed.');
            err.original = e;
            throw err;
        }
    }

    /**
     * Vue plugin installation.
     * Makes the helper available as `this.$compress` in Vue components.
     * @param {any} Vue - Vue application instance.
     */
    install(Vue) {
        if (Vue.config?.globalProperties) {
            Vue.config.globalProperties.$compress = this;
            Vue.provide('CompressionHelper', this);
        } else if (Vue.prototype) {
            Vue.prototype.$compress = this;
        } else {
            throw new Error('[CompressionHelper] Unsupported Vue version.');
        }
    }
}

const instance = new CompressionHelper();
export default instance;
